import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useFormik } from 'formik';
import { useHistory } from 'react-router-dom';
import { Card, Container, Label } from 'reactstrap';

import AppInputValidation from 'components/App/InputValidation';
import AppSelectValidation from 'components/App/SelectValidation';
import Breadcrumb from 'components/App/Breadcrumb';
import Button from 'components/App/Button';
import DataTableVoucher from 'components/App/DataTableVoucher';
import Meta from 'components/App/Meta';
import AppLoading from 'components/App/Loading';
import Pagination from 'components/Pagination';
import ModalRequestAction from 'components/Modals/ModalRequestAction';
import totalPage from 'utils/totalPage';

import images from 'assets/images';
import { themeColor } from 'constants/index';
import ENDPOINT from 'constants/endPoint';
import { getParamsFormObject } from 'utils';
import cn from 'utils/classnames';
import AddWhiteIcon from 'assets/images/add-white.svg';

import styles from './ManagerVoucher.module.scss';

const PAGE_TITLE = 'Quản lý chiến dịch';

const voucherCountFormatter = new Intl.NumberFormat('vi-VN', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

const VOUCHER_TYPE_OPTIONS = [
  { value: 'BOOKESE', label: 'BOOKESE' },
  { value: 'PARTNER', label: 'PARTNER' },
];

const breadcrumbList = [
  {
    name: 'Quà tặng',
    isActive: false,
    link: ENDPOINT.MANAGER_GIFT,
  },
  {
    name: PAGE_TITLE,
    isActive: true,
    link: '',
  },
];

function ManagerVoucher({
  voucherList,
  voucherTotal,
  isLoadingGetVoucherList,
  isLoadingDeleteVoucherCampaign,
  actionGetListVoucher,
  actionDeleteVoucherCampaign,
}) {
  const history = useHistory();

  const searchQueries = useMemo(() => {
    const params = new URLSearchParams(history.location.search);
    return {
      page: Number(params.get('page')) || 1,
      q: params.get('q') || '',
      useType: params.get('useType') || '',
    };
  }, [history.location.search]);

  useEffect(() => {
    actionGetListVoucher({
      q: searchQueries.q,
      useType: searchQueries.useType,
      page: searchQueries.page,
      limit: 10,
    });
  }, [actionGetListVoucher, searchQueries]);

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      q: searchQueries.q,
      useType: searchQueries.useType,
    },
    onSubmit: (values) => {
      const urlParams = { page: '1' };
      const q = values.q.trim();
      if (q) urlParams.q = q;
      if (values.useType) urlParams.useType = values.useType;
      history.push({
        pathname: ENDPOINT.MANAGER_GIFT_VOUCHER,
        search: getParamsFormObject({ ...urlParams }),
      });
    },
  });

  const onChangeFilterOption = useCallback(
    (e) => {
      validation.setFieldValue(e.name, e.value, true);
    },
    [validation],
  );

  const onResetForm = useCallback(() => {
    validation.resetForm();
    history.replace({
      pathname: ENDPOINT.MANAGER_GIFT_VOUCHER,
      search: '',
    });
  }, [history, validation]);

  const onChangePage = useCallback(
    (selected) => {
      history.push({
        pathname: ENDPOINT.MANAGER_GIFT_VOUCHER,
        search: getParamsFormObject({
          ...searchQueries,
          page: selected.selected + 1,
        }),
      });
    },
    [history, searchQueries],
  );

  const onViewVoucherDetail = useCallback(
    (voucher) => {
      history.push(ENDPOINT.MANAGER_GIFT_VOUCHER_CAMPAIGN_DETAIL(voucher._id));
    },
    [history],
  );

  const onAddCampaign = useCallback(() => {
    history.push(ENDPOINT.MANAGER_GIFT_VOUCHER_CAMPAIGN);
  }, [history]);

  const [isOpenDeleteCampaignModal, setIsOpenDeleteCampaignModal] =
    useState(false);
  const [selectedVoucher, setSelectedVoucher] = useState(null);

  const onDeleteVoucher = useCallback((voucher) => {
    setSelectedVoucher(voucher);
    setIsOpenDeleteCampaignModal(true);
  }, []);

  const onCloseDeleteCampaignModal = useCallback(() => {
    setIsOpenDeleteCampaignModal(false);
    setSelectedVoucher(null);
  }, []);

  const onConfirmDeleteCampaign = useCallback(() => {
    if (!selectedVoucher?._id) return;
    actionDeleteVoucherCampaign({
      id: selectedVoucher._id,
      callback: () => {
        actionGetListVoucher({
          q: searchQueries.q,
          useType: searchQueries.useType,
          page: searchQueries.page,
          limit: 10,
        });
        onCloseDeleteCampaignModal();
      },
    });
  }, [
    actionDeleteVoucherCampaign,
    actionGetListVoucher,
    searchQueries,
    selectedVoucher,
    onCloseDeleteCampaignModal,
  ]);

  const isSearchBtnDisabled =
    !validation.values.q.trim() && !validation.values.useType;

  const voucherCountLabel = `${voucherCountFormatter.format(voucherTotal)} voucher`;

  return (
    <div className={`page-content ${styles.Page}`}>
      <Meta name={PAGE_TITLE} />

      <Container fluid>
        <Breadcrumb pageTitle={PAGE_TITLE} breadcrumbList={breadcrumbList} />

        <Card className="Section">
          <form onSubmit={validation.handleSubmit}>
            <div className={styles.FilterRow}>
              <div className={styles.FilterFields}>
                <div className={styles.FilterSearchCol}>
                  <Label className="Section__Search__Label">Tìm kiếm</Label>

                  <div className="Section__Search__Container">
                    <AppInputValidation
                      name="q"
                      id="q"
                      type="text"
                      placeholder="Tìm kiếm theo Mã code"
                      validation={validation}
                      inputClassName={cn(
                        'Section__Search__Input',
                        styles.FilterSearchInput,
                      )}
                    />

                    <i className="fas fa-search Section__Search__Icon__Search" />
                  </div>
                </div>

                <div className={styles.FilterSelectCol}>
                  <Label className="Section__Search__Label">Bộ lọc</Label>

                  <AppSelectValidation
                    placeholder="Theo Loại voucher"
                    name="useType"
                    keyName="useType"
                    options={VOUCHER_TYPE_OPTIONS}
                    validation={validation}
                    value={validation.values.useType}
                    onChange={onChangeFilterOption}
                    isHiddenSearchable
                    isHiddenClearable
                    selectedClassName={styles.VoucherTypeSelect}
                  />
                </div>
              </div>

              <div className={styles.FilterActionsCol}>
                <Button
                  outline
                  classes="Section__Search__Button__ReType mr-24"
                  color={themeColor.LIGHT}
                  onClick={onResetForm}
                >
                  Nhập lại
                </Button>

                <Button
                  classes="Section__Search__Button__Search"
                  type="submit"
                  disabled={isSearchBtnDisabled}
                >
                  Tìm kiếm
                </Button>
              </div>
            </div>
          </form>
        </Card>

        <Card className="Section">
          <div className="Tab__Header__Container">
            <Label className="Table__Body__Label">{voucherCountLabel}</Label>

            <Button
              type="button"
              classes="Tab__Header__Button"
              onClick={onAddCampaign}
            >
              <img src={AddWhiteIcon} alt="Add icon" />
              <span className="Tab__Header__Button__Text">Thêm chiến dịch</span>
            </Button>
          </div>

          {isLoadingGetVoucherList ? (
            <AppLoading loading={isLoadingGetVoucherList} loaderOnPage />
          ) : (
            <div className={styles.TableScroll}>
              <DataTableVoucher
                voucherList={voucherList}
                onViewVoucherDetail={onViewVoucherDetail}
                onDeleteVoucher={onDeleteVoucher}
              />
            </div>
          )}
        </Card>

        {voucherTotal > 0 && (
          <Pagination
            page={searchQueries.page}
            pageCount={totalPage(voucherTotal, 10)}
            onChangePage={onChangePage}
          />
        )}
      </Container>

      <ModalRequestAction
        isOpen={isOpenDeleteCampaignModal}
        icon={images.modalGarbageCan.default}
        textRequest={
          'Bạn có chắc chắn muốn xóa \n chiến dịch voucher này không?'
        }
        onCloseModal={onCloseDeleteCampaignModal}
        onConfirm={onConfirmDeleteCampaign}
        isLoadingConfirm={isLoadingDeleteVoucherCampaign}
      />
    </div>
  );
}

ManagerVoucher.propTypes = {
  isLoadingGetVoucherList: PropTypes.bool,
  isLoadingDeleteVoucherCampaign: PropTypes.bool,
  voucherTotal: PropTypes.number,
  actionGetListVoucher: PropTypes.func,
  actionDeleteVoucherCampaign: PropTypes.func,
  voucherList: PropTypes.arrayOf(
    PropTypes.shape({
      _id: PropTypes.string.isRequired,
      code: PropTypes.string,
      name: PropTypes.string,
      useType: PropTypes.string,
      startAt: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.instanceOf(Date),
      ]),
      endAt: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.instanceOf(Date),
      ]),
      voucherExpiryAt: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.instanceOf(Date),
      ]),
      totalVoucher: PropTypes.number,
      usedVoucher: PropTypes.number,
    }),
  ),
};

ManagerVoucher.defaultProps = {
  isLoadingGetVoucherList: false,
  isLoadingDeleteVoucherCampaign: false,
  voucherTotal: 0,
  actionGetListVoucher: undefined,
  actionDeleteVoucherCampaign: undefined,
  voucherList: [],
};

export default ManagerVoucher;
